import React from 'react';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ApiClientProvider } from '@fanini/shared-api';
import { queryClient } from '@shared/api';
import { config } from '@shared/config';
import { routeTree } from '../routeTree.gen';

const router = createRouter({ routeTree, context: { queryClient } });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Innerer Provider der innerhalb von ClerkProvider liegt.
 * Hier kann `useAuth()` sicher aufgerufen werden.
 * Entfernt das /api-Suffix aus config.apiUrl da der shared-api Client
 * Paths wie /api/events selbst voranstellt.
 */
function AppProviders({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();

  // config.apiUrl ist z.B. "https://api.fanini.live/api" — wir brauchen nur "https://api.fanini.live"
  const baseUrl = config.apiUrl.replace(/\/api\/?$/, '');

  return (
    <ApiClientProvider baseUrl={baseUrl} getToken={() => getToken()}>
      {children}
    </ApiClientProvider>
  );
}

/**
 * Root App-Komponente mit Clerk Auth, TanStack Query, ApiClient und Router Providern.
 *
 * @returns Die vollstaendig konfigurierte App-Shell.
 */
export const App = () => (
  <ClerkProvider publishableKey={config.clerkPublishableKey}>
    <QueryClientProvider client={queryClient}>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </QueryClientProvider>
  </ClerkProvider>
);
